/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `raw_bbi` message.
 *
 * Raw Beat-to-Beat Interval values
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class RawBbiMesg : Mesg {
    public constructor() : super("RawBbi", Profile.MesgNum.RAW_BBI)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond resolution of the timestamp */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in data. */
    public val numData: Int
        get() = getField(DATA_FIELD_NUM)?.numValues ?: 0

    /** data. 1 bit for gap indicator, 1 bit for quality indicator, and 14 bits for Beat-to-Beat interval values in whole-integer millisecond resolution */
    public var data: List<UShort?>?
        get() {
            val field = getField(DATA_FIELD_NUM) ?: return null
            return List(field.numValues) { getData(it) }
        }
        set(value) {
            removeField(DATA_FIELD_NUM)
            value?.forEachIndexed { index, element -> setData(index, element) }
        }

    public fun getData(index: Int): UShort? =
        getFieldValue(DATA_FIELD_NUM, index) as? UShort

    public fun setData(index: Int, value: UShort?) {
        setFieldValue(DATA_FIELD_NUM, value, index)
    }

    /** Number of values stored in time. */
    public val numTime: Int
        get() = getField(TIME_FIELD_NUM)?.numValues ?: 0

    /** time, in ms. Array of millisecond times between beats */
    public var time: List<UShort?>?
        get() {
            val field = getField(TIME_FIELD_NUM) ?: return null
            return List(field.numValues) { getTime(it) }
        }
        set(value) {
            removeField(TIME_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTime(index, element) }
        }

    public fun getTime(index: Int): UShort? =
        getFieldValue(TIME_FIELD_NUM, index) as? UShort

    public fun setTime(index: Int, value: UShort?) {
        setFieldValue(TIME_FIELD_NUM, value, index)
    }

    /** Number of values stored in quality. */
    public val numQuality: Int
        get() = getField(QUALITY_FIELD_NUM)?.numValues ?: 0

    /** quality. 1 = high confidence. 0 = low confidence. N/A when gap = 1 */
    public var quality: List<UByte?>?
        get() {
            val field = getField(QUALITY_FIELD_NUM) ?: return null
            return List(field.numValues) { getQuality(it) }
        }
        set(value) {
            removeField(QUALITY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setQuality(index, element) }
        }

    public fun getQuality(index: Int): UByte? =
        getFieldValue(QUALITY_FIELD_NUM, index) as? UByte

    public fun setQuality(index: Int, value: UByte?) {
        setFieldValue(QUALITY_FIELD_NUM, value, index)
    }

    /** Number of values stored in gap. */
    public val numGap: Int
        get() = getField(GAP_FIELD_NUM)?.numValues ?: 0

    /** gap. 1 = gap (time represents ms gap length). 0 = BBI data */
    public var gap: List<UByte?>?
        get() {
            val field = getField(GAP_FIELD_NUM) ?: return null
            return List(field.numValues) { getGap(it) }
        }
        set(value) {
            removeField(GAP_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGap(index, element) }
        }

    public fun getGap(index: Int): UByte? =
        getFieldValue(GAP_FIELD_NUM, index) as? UByte

    public fun setGap(index: Int, value: UByte?) {
        setFieldValue(GAP_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val DATA_FIELD_NUM: UByte = 1u
        public const val TIME_FIELD_NUM: UByte = 2u
        public const val QUALITY_FIELD_NUM: UByte = 3u
        public const val GAP_FIELD_NUM: UByte = 4u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("Data", 1u, BaseType.UINT16, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(2u, false, 14, 1.0, 0.0))
                .addComponent(FieldComponent(3u, false, 1, 1.0, 0.0))
                .addComponent(FieldComponent(4u, false, 1, 1.0, 0.0))
            fields[2] = Field("Time", 2u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[3] = Field("Quality", 3u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[4] = Field("Gap", 4u, BaseType.UINT8, 1.0, 0.0, "", false)
            return fields
        }
    }
}
